import i18n from '@/i18n';
import { taskAPI } from '@/utils/api';
import { isManagedStorageUrl } from '@/utils/imageDisplayAccess';
import { resolveSignedDisplayUrl } from '@/utils/imageUrlUtils';
import {
  VIDEO_CONCEPT_PRESET_LABELS,
  formatAgentVideoPhaseLabel,
} from '@/pages/ipe/workbench/constants/videoTaskResultLabels';
import {
  getVideoGenerationModeLabel,
  normalizeVideoAspectRatio,
} from '@/pages/ipe/workbench/constants/videoStudioConfig';

export function isVideoStudioNavId(navId) {
  return navId === 'videos' || navId === 'textToVideo';
}

export function resolveVideoStudioNavIdFromTask(task, fallback = 'videos') {
  const type = String(task?.type || '').trim();
  if (type === 'textToVideo') return 'textToVideo';
  if (type === 'videos') return 'videos';
  return isVideoStudioNavId(fallback) ? fallback : 'videos';
}

export function normalizeVideoTaskResult(task) {
  const raw = task?.result;
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) return raw;
  return {};
}

/** 静帧/封面扩展名：不可当作成片 URL（避免首帧 jpg 误入 <video>） */
export function isLikelyStillImageUrl(u) {
  if (!u) return false;
  return /\.(jpe?g|png|gif|webp|bmp|svg)(\?|#|$)/i.test(String(u));
}

export function pickVideoUrl(result) {
  const r = result || {};
  const keys = ['outputVideoUrl', 'aiVideoUrl', 'previewVideoUrl', 'videoUrl', 'generatedVideoUrl'];
  for (const k of keys) {
    const v = r[k];
    if (v == null || String(v).trim() === '') continue;
    const next = String(v).trim();
    // 跳过误写入的封面/预览图
    if (isLikelyStillImageUrl(next)) continue;
    return next;
  }
  return '';
}

export function pickFallbackDemoUrl(result) {
  const v = result?.fallbackDemoVideoUrl;
  return v != null && String(v).trim() !== '' ? String(v).trim() : '';
}

export function pickTryOnImageUrl(result) {
  const v = result?.outputTryOnImageUrl;
  return v != null && String(v).trim() !== '' ? String(v).trim() : '';
}

export function isVideoFileUrl(u) {
  if (!u) return false;
  return /\.(mp4|webm|ogg|mov)(\?|#|$)/i.test(String(u));
}

function isManagedUploadsPath(url) {
  const s = String(url || '').trim();
  if (!s) return false;
  if (s.startsWith('/uploads/') || s.startsWith('/api/uploads/')) return true;
  try {
    if (/^https?:\/\//i.test(s)) {
      const path = new URL(s).pathname || '';
      return path.startsWith('/uploads/') || path.startsWith('/api/uploads/');
    }
  } catch {
    /* ignore */
  }
  return false;
}

/**
 * 成片播放地址：受管路径走签名；外链（万相临时链）直出。
 * 勿把 https 一律当直链——OSS 受管 https 仍需 sign-batch。
 */
export async function resolveManagedMediaDisplayUrl(rawUrl) {
  const raw = String(rawUrl || '').trim();
  if (!raw) return '';
  if (/^(data:|blob:)/i.test(raw)) return raw;
  const needsSign = isManagedStorageUrl(raw) || isManagedUploadsPath(raw);
  if (/^https?:\/\//i.test(raw) && !needsSign) return raw;
  const resolved = await resolveSignedDisplayUrl(raw);
  return String(resolved || raw).trim();
}

/** 比较是否为同一路径（忽略 host，便于判断成片 URL 是否仍为演示文件） */
export function urlPathKey(u) {
  if (u == null) return '';
  const s = String(u).trim();
  if (!s) return '';
  try {
    if (/^https?:\/\//i.test(s)) {
      return new URL(s).pathname || s;
    }
  } catch (_) {
    /* ignore */
  }
  return s.startsWith('/') ? s : `/${s}`;
}

export function aspectRatioCssValue(ratio) {
  const normalized = normalizeVideoAspectRatio(ratio);
  const [w, h] = normalized.split(':').map((n) => Number(n));
  if (!w || !h) return '9 / 16';
  return `${w} / ${h}`;
}

function pickItemVideoUrl(item) {
  if (item == null) return '';
  if (typeof item === 'string') {
    const s = String(item).trim();
    return s && !isLikelyStillImageUrl(s) ? s : '';
  }
  if (typeof item !== 'object') return '';
  const keys = ['outputVideoUrl', 'aiVideoUrl', 'previewVideoUrl', 'videoUrl', 'generatedVideoUrl', 'url', 'src'];
  for (const k of keys) {
    const v = item[k];
    if (v == null || String(v).trim() === '') continue;
    const next = String(v).trim();
    if (isLikelyStillImageUrl(next)) continue;
    return next;
  }
  return '';
}

/** 从任务 result 收集成片列表（兼容单 URL / URL 数组 / 分组 videoUnits） */
export function collectOutputVideos(result, { allowFallbackDemo = true } = {}) {
  const r = result || {};
  const items = [];
  const pushUrl = (url, label, extra = {}) => {
    const next = String(url || '').trim();
    if (!next || isLikelyStillImageUrl(next)) return;
    if (items.some((x) => x.url === next)) return;
    items.push({
      id: `video-${items.length + 1}`,
      url: next,
      label: label || i18n.t('workbench:detailChips.nameVideoNth', { index: items.length + 1 }),
      kind: 'video',
      posterUrl: String(extra.posterUrl || '').trim() || undefined,
      groupIndex: extra.groupIndex,
    });
  };

  const units = Array.isArray(r.videoUnits) ? r.videoUnits : [];
  if (units.length > 0) {
    units.forEach((unit, index) => {
      const groupIndex = Number.isFinite(Number(unit?.groupIndex))
        ? Number(unit.groupIndex)
        : index;
      pushUrl(
        pickItemVideoUrl(unit),
        i18n.t('workbench:detailChips.nameVideoNth', { index: groupIndex + 1 }),
        {
          posterUrl: unit?.outputVideoPosterUrl || unit?.previewUrl,
          groupIndex,
        },
      );
    });
  }

  const arrayCandidates = [r.outputVideoUrls, r.videos, r.outputs, r.videoOutputs];
  arrayCandidates.forEach((arr) => {
    if (!Array.isArray(arr)) return;
    arr.forEach((entry, index) => {
      pushUrl(
        pickItemVideoUrl(entry),
        i18n.t('workbench:detailChips.nameVideoNth', { index: index + 1 }),
      );
    });
  });

  if (items.length === 0) {
    pushUrl(pickVideoUrl(r), i18n.t('workbench:detailChips.nameVideoNth', { index: 1 }));
  }

  if (items.length === 0 && allowFallbackDemo) {
    pushUrl(pickFallbackDemoUrl(r), i18n.t('workbench:detailChips.nameDemoVideo'));
  }

  return items;
}

/** 分组图生详情：每组参考图 + 对应成片 */
export function buildVideoI2vGroupPairs(result) {
  const r = result && typeof result === 'object' ? result : {};
  const storedGroups = Array.isArray(r.imageGroups) ? r.imageGroups : [];
  const units = Array.isArray(r.videoUnits) ? r.videoUnits : [];
  if (storedGroups.length === 0 && units.length === 0) return [];

  const count = Math.max(storedGroups.length, units.length, 1);
  const pairs = [];
  for (let i = 0; i < count; i += 1) {
    const group = storedGroups[i] || {};
    const unit = units[i] || {};
    const urls = Array.isArray(group.urls) ? group.urls : [];
    const previews = Array.isArray(group.previews) ? group.previews : [];
    const names = Array.isArray(group.names) ? group.names : [];
    const unitPreviews = Array.isArray(unit.referencePreviews) ? unit.referencePreviews : [];
    const countImgs = Math.max(urls.length, previews.length, unitPreviews.length);
    const referenceImageItems = [];
    for (let j = 0; j < countImgs; j += 1) {
      const preview = String(previews[j] || unitPreviews[j] || urls[j] || '').trim();
      const apiUrl = String(urls[j] || previews[j] || unitPreviews[j] || '').trim();
      if (!preview && !apiUrl) continue;
      referenceImageItems.push({
        url: apiUrl || preview,
        previewUrl: preview || apiUrl,
        name: String(names[j] || '').trim()
          || i18n.t('workbench:detailChips.nameImageNth', { index: j + 1 }),
      });
    }
    if (referenceImageItems.length === 0) {
      const fallback = String(
        unit.firstFrameUrl || r.referencePreviews?.[i] || r.referenceUrls?.[i] || '',
      ).trim();
      if (fallback) {
        referenceImageItems.push({
          url: fallback,
          previewUrl: fallback,
          name: i18n.t('workbench:detailChips.nameGroupFirstFrame', { index: i + 1 }),
        });
      }
    }
    const videoUrl = pickItemVideoUrl(unit) || '';
    const posterUrl = String(unit.outputVideoPosterUrl || unit.previewUrl || '').trim();
    pairs.push({
      groupIndex: Number.isFinite(Number(unit.groupIndex)) ? Number(unit.groupIndex) : i,
      referencePreview: referenceImageItems[0]?.previewUrl || '',
      referenceImages: referenceImageItems.map((item) => item.previewUrl || item.url),
      referenceImageItems,
      extraCount: Math.max(0, referenceImageItems.length - 1),
      outputVideoUrl: videoUrl,
      outputVideoPosterUrl: posterUrl,
      unitPhase: String(unit.agentVideoPhase || '').trim(),
      unitError: String(unit.aigcError || '').trim(),
    });
  }
  return pairs.filter((pair) => (
    pair.referenceImageItems.length > 0 || pair.outputVideoUrl
  ));
}

export function extFromVideoUrl(url) {
  const match = String(url || '').match(/\.(mp4|webm|ogg|mov)(\?|#|$)/i);
  if (match) return `.${match[1].toLowerCase()}`;
  return '.mp4';
}

export function buildVideoStudioParamsChips(result, { isTextToVideo = false } = {}) {
  const chips = [];
  const r = result || {};
  // 旧任务兼容：仍展示历史概念/数量
  if (!isTextToVideo) {
    let concept = '';
    if (r.conceptMode === 'custom') {
      const t = r.customPrompt != null ? String(r.customPrompt).trim() : '';
      concept = t || i18n.t('workbench:videoDetail.custom');
    } else if (r.conceptPresetId) {
      concept = VIDEO_CONCEPT_PRESET_LABELS[r.conceptPresetId] || String(r.conceptPresetId);
    }
    if (concept) {
      chips.push({
        label: i18n.t('workbench:videoDetail.concept'),
        value: concept.length > 80 ? `${concept.slice(0, 80)}…` : concept,
      });
    }
    if (r.quantity != null) {
      chips.push({ label: i18n.t('workbench:videoDetail.quantity'), value: String(r.quantity) });
    }
  }
  const mode = r.generationMode ? getVideoGenerationModeLabel(r.generationMode) : '';
  if (mode) chips.push({ label: i18n.t('workbench:videoDetail.quality'), value: mode });
  if (r.aspectRatio) {
    chips.push({ label: i18n.t('workbench:videoDetail.ratio'), value: String(r.aspectRatio) });
  }
  if (r.durationSec != null) {
    chips.push({ label: i18n.t('workbench:videoDetail.duration'), value: `${r.durationSec}s` });
  }
  if (r.seed != null && r.seed !== '') {
    chips.push({ label: i18n.t('workbench:videoDetail.seed'), value: String(r.seed) });
  }
  return chips;
}

export function resolveVideoStudioPromptText(result, { isTextToVideo = false } = {}) {
  const r = result || {};
  if (isTextToVideo) {
    return String(r.scriptPrompt || r.prompt || r.customPrompt || '').trim();
  }
  const supplement = String(r.supplementPrompt || '').trim();
  if (supplement) return supplement;
  if (r.conceptMode === 'custom') {
    return String(r.customPrompt || '').trim();
  }
  if (r.conceptPresetId) {
    return VIDEO_CONCEPT_PRESET_LABELS[r.conceptPresetId] || String(r.conceptPresetId);
  }
  return String(r.scriptPrompt || r.prompt || r.customPrompt || '').trim();
}

export function getVideoStudioOutputCount(result, fallbackTotal) {
  const r = result || {};
  const units = Array.isArray(r.videoUnits) ? r.videoUnits : [];
  if (units.length > 0) return units.length;
  const items = collectOutputVideos(result, { allowFallbackDemo: true });
  if (items.length > 0) return items.length;
  if (pickTryOnImageUrl(result)) return 1;
  const n = Number(fallbackTotal);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

export function buildVideoStudioModeSubtitle({ isTextToVideo }) {
  return isTextToVideo
    ? i18n.t('workbench:detailChips.subVideoT2v')
    : i18n.t('workbench:detailChips.subVideoI2v');
}

export function resolveVideoStudioProgressHint(task, result) {
  const status = String(task?.status || '').toLowerCase();
  const stillProcessing = ['processing', 'pending', 'executing', 'new'].includes(status);
  if (!stillProcessing) return undefined;
  const r = result || {};
  if (r.queueWaitMessage) return String(r.queueWaitMessage);
  if (
    String(r.agentVideoPhase || '').toUpperCase() === 'QUEUE_WAIT'
    || String(r.batchPhase || '').toLowerCase() === 'queued'
  ) {
    return i18n.t('workbench:ipeUtilError.queueBusy');
  }
  if (r.agentVideoPhase) {
    return i18n.t('workbench:ipeUtilError.generatingWithPhase', { phase: formatAgentVideoPhaseLabel(r.agentVideoPhase) });
  }
  return i18n.t('workbench:ipeUtilError.generatingEllipsis');
}

export function canCancelVideoStudioTask(task, result) {
  const status = String(task?.status || '').toLowerCase();
  const stillProcessing = ['processing', 'pending', 'executing', 'new'].includes(status);
  if (!stillProcessing) return false;
  const r = result || {};
  return Boolean(
    !pickVideoUrl(r)
      && (r.agentVideoTaskId || r.agentVideoPhase || !r.lingtuDressJobId),
  );
}

/** 视频详情加载：videos / textToVideo 互通，避免类型不一致导致 miss */
export async function resolveVideoStudioTaskByBatchKey(batchKey, preferredType = 'videos') {
  const key = String(batchKey || '').trim();
  if (!key) return null;
  try {
    const { task } = await taskAPI.getById(key);
    if (task && isVideoStudioNavId(String(task.type || '').trim())) {
      return task;
    }
  } catch {
    /* fallback below */
  }
  const preferred = preferredType === 'textToVideo' ? 'textToVideo' : 'videos';
  const order = preferred === 'textToVideo' ? ['textToVideo', 'videos'] : ['videos', 'textToVideo'];
  for (const type of order) {
    try {
      const { tasks = [] } = await taskAPI.getAll({ type, pageSize: 500 });
      const found = tasks.find(
        (row) => String(row.subAccount) === key || String(row.id) === key,
      );
      if (found) return found;
    } catch {
      /* try next */
    }
  }
  return null;
}
